import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, X } from 'lucide-react'
import { useUIStore } from '@/lib/store'
import { cn } from '@/lib/utils'
import { Button } from './Button'

interface Command {
  id: string
  title: string
  description?: string
  icon?: React.ReactNode
  action: () => void
  keywords?: string[]
}

interface CommandPaletteProps {
  commands: Command[]
  className?: string
}

export function CommandPalette({ commands, className }: CommandPaletteProps) {
  const { isCommandPaletteOpen, setCommandPaletteOpen } = useUIStore()
  const [search, setSearch] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)

  const filteredCommands = commands.filter((command) => {
    const searchLower = search.toLowerCase()
    return (
      command.title.toLowerCase().includes(searchLower) ||
      command.description?.toLowerCase().includes(searchLower) ||
      command.keywords?.some((keyword) =>
        keyword.toLowerCase().includes(searchLower)
      )
    )
  })

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isCommandPaletteOpen) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) =>
          prev < filteredCommands.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredCommands.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (filteredCommands[selectedIndex]) {
          filteredCommands[selectedIndex].action()
          setCommandPaletteOpen(false)
          setSearch('')
          setSelectedIndex(0)
        }
        break
      case 'Escape':
        e.preventDefault()
        setCommandPaletteOpen(false)
        setSearch('')
        setSelectedIndex(0)
        break
    }
  }

  useEffect(() => {
    if (isCommandPaletteOpen) {
      setSelectedIndex(0)
    }
  }, [isCommandPaletteOpen, search])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setCommandPaletteOpen(!isCommandPaletteOpen)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isCommandPaletteOpen, setCommandPaletteOpen])

  return (
    <AnimatePresence>
      {isCommandPaletteOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
          onClick={() => setCommandPaletteOpen(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.2 }}
            className={cn(
              'fixed left-1/2 top-1/4 w-full max-w-lg -translate-x-1/2 -translate-y-1/2',
              className
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card p-0 overflow-hidden">
              <div className="flex items-center border-b border-border-light dark:border-border-dark px-4 py-3">
                <Search className="h-4 w-4 text-text-light/60 dark:text-text-dark/60 mr-3" />
                <input
                  type="text"
                  placeholder="Type a command or search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 bg-transparent text-text-light dark:text-text-dark placeholder:text-text-light/60 dark:placeholder:text-text-dark/60 focus:outline-none"
                  autoFocus
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCommandPaletteOpen(false)}
                  className="ml-2"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {filteredCommands.length === 0 ? (
                  <div className="px-4 py-8 text-center text-text-light/60 dark:text-text-dark/60">
                    No commands found
                  </div>
                ) : (
                  <div className="py-2">
                    {filteredCommands.map((command, index) => (
                      <button
                        key={command.id}
                        onClick={() => {
                          command.action()
                          setCommandPaletteOpen(false)
                          setSearch('')
                          setSelectedIndex(0)
                        }}
                        className={cn(
                          'w-full flex items-center px-4 py-3 text-left transition-colors',
                          index === selectedIndex
                            ? 'bg-accent/10 text-accent'
                            : 'hover:bg-accent/5'
                        )}
                      >
                        {command.icon && (
                          <div className="mr-3 text-text-light/60 dark:text-text-dark/60">
                            {command.icon}
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="font-medium">{command.title}</div>
                          {command.description && (
                            <div className="text-sm text-text-light/60 dark:text-text-dark/60">
                              {command.description}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
